import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Star, RefreshCw } from 'lucide-react';
import IconButton from '../components/ui/IconButton.jsx';
import Modal from '../components/ui/Modal.jsx';
import Pill from '../components/ui/Pill.jsx';
import { api } from '../api/client.js';
import { formatDateFr } from '../lib/dates.js';

const EMPTY_TEMPLATE = { name: '', subject: '', body: '', isDefault: false };

export default function TemplatesView({ user }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const isAdmin = user?.isAdmin === true;

  const load = () => {
    setLoading(true);
    api.mail
      .listTemplates()
      .then(({ templates }) => setTemplates(templates))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const save = async () => {
    try {
      if (editing.id) {
        await api.mail.updateTemplate(editing.id, {
          name: editing.name,
          subject: editing.subject,
          body: editing.body,
          isDefault: editing.isDefault,
        });
      } else {
        await api.mail.createTemplate({
          name: editing.name,
          subject: editing.subject,
          body: editing.body,
          isDefault: editing.isDefault,
        });
      }
      setEditing(null);
      load();
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    }
  };

  const remove = async () => {
    try {
      await api.mail.deleteTemplate(confirmDelete.id);
      setConfirmDelete(null);
      load();
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    }
  };

  const setDefault = async (template) => {
    try {
      await api.mail.updateTemplate(template.id, { isDefault: true });
      load();
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <section style={cardStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <h2 style={sectionTitleStyle}>Templates d'email ({templates.length})</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <IconButton icon={RefreshCw} title="Rafraîchir" onClick={load} />
            {isAdmin && (
              <button
                onClick={() => setEditing({ ...EMPTY_TEMPLATE })}
                style={primaryButtonStyle(false)}
              >
                <Plus size={16} /> Nouveau template
              </button>
            )}
          </div>
        </div>

        {!isAdmin && (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            Lecture seule — seul un administrateur peut créer ou modifier les templates.
          </p>
        )}

        {loading && <p style={{ color: 'var(--text-muted)' }}>Chargement…</p>}

        {!loading && templates.length === 0 && (
          <p style={{ color: 'var(--text-muted)' }}>
            Aucun template enregistré. {isAdmin && 'Créez-en un pour commencer.'}
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {templates.map((t) => (
            <div
              key={t.id}
              style={{
                padding: '1rem',
                background: 'var(--glass)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  marginBottom: '0.5rem',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <strong style={{ color: 'var(--text)' }}>{t.name}</strong>
                    {t.isDefault && (
                      <span
                        style={{
                          fontSize: '0.7rem',
                          padding: '0.15rem 0.5rem',
                          background: 'var(--primary)',
                          color: '#fff',
                          borderRadius: 'var(--radius-pill)',
                        }}
                      >
                        Par défaut
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    Sujet : {t.subject}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                    Mis à jour le {formatDateFr(t.updatedAt)}
                  </div>
                </div>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    {!t.isDefault && (
                      <IconButton
                        icon={Star}
                        title="Définir comme template par défaut"
                        onClick={() => setDefault(t)}
                        color="var(--warning)"
                      />
                    )}
                    <IconButton icon={Pencil} title="Modifier" onClick={() => setEditing(t)} />
                    <IconButton
                      icon={Trash2}
                      title="Supprimer"
                      onClick={() => setConfirmDelete(t)}
                      color="var(--danger)"
                    />
                  </div>
                )}
              </div>
              <pre
                style={{
                  margin: 0,
                  fontSize: '0.8rem',
                  color: 'var(--text-muted)',
                  fontFamily: 'inherit',
                  whiteSpace: 'pre-wrap',
                  background: 'transparent',
                }}
              >
                {t.body}
              </pre>
            </div>
          ))}
        </div>
      </section>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Modifier le template' : 'Créer un template'}
        size="lg"
        footer={
          <>
            <button onClick={() => setEditing(null)} style={secondaryButtonStyle}>
              Annuler
            </button>
            <button
              onClick={save}
              disabled={!editing?.name || !editing?.subject || !editing?.body}
              style={primaryButtonStyle(!editing?.name || !editing?.subject || !editing?.body)}
            >
              {editing?.id ? 'Enregistrer' : 'Créer'}
            </button>
          </>
        }
      >
        {editing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label style={labelStyle}>
              Nom (référence interne)
              <input
                type="text"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              Sujet
              <input
                type="text"
                value={editing.subject}
                onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                style={inputStyle}
                placeholder="Ex: Votre CRA Synotis - {mois}"
              />
            </label>
            <label style={labelStyle}>
              Corps du message
              <textarea
                rows={10}
                value={editing.body}
                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
              />
            </label>
            <Pill
              active={editing.isDefault}
              onClick={() => setEditing({ ...editing, isDefault: !editing.isDefault })}
            >
              Template par défaut
            </Pill>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: 0 }}>
              Variables disponibles : <code>{'{collaborateur}'}</code>, <code>{'{client}'}</code>,{' '}
              <code>{'{mois}'}</code>, <code>{'{periode}'}</code>
            </p>
          </div>
        )}
      </Modal>

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title="Supprimer le template"
        footer={
          <>
            <button onClick={() => setConfirmDelete(null)} style={secondaryButtonStyle}>
              Annuler
            </button>
            <button onClick={remove} style={{ ...primaryButtonStyle(false), background: 'var(--danger)' }}>
              <Trash2 size={16} /> Supprimer
            </button>
          </>
        }
      >
        <p>
          Supprimer le template <strong>{confirmDelete?.name}</strong> ? Cette action est définitive.
        </p>
      </Modal>
    </div>
  );
}

const cardStyle = {
  background: 'var(--card-bg)',
  padding: '1.5rem',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-card)',
};
const sectionTitleStyle = { margin: 0, fontSize: '1rem', color: 'var(--primary)' };
const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  color: 'var(--text-muted)',
  fontSize: '0.85rem',
};
const inputStyle = {
  padding: '0.5rem 0.75rem',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--glass-border)',
  background: 'var(--glass)',
  color: 'var(--text)',
  fontSize: '0.9rem',
};
const primaryButtonStyle = (disabled) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.6rem 1.25rem',
  background: disabled ? 'var(--text-muted)' : 'var(--primary)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '0.9rem',
  fontWeight: 600,
});
const secondaryButtonStyle = {
  padding: '0.6rem 1.25rem',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  fontSize: '0.9rem',
};
